import React, { useEffect, useState, useRef, useMemo } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import barangays from './barangays.json';
import { FaPen, FaPlus, FaTrash } from 'react-icons/fa6';
import Preloader from './Preloader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

// Add CSS animations
const styles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slideIn {
    from { transform: translateX(-100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
    40%, 43% { transform: translate3d(0,-8px,0); }
    70% { transform: translate3d(0,-4px,0); }
    90% { transform: translate3d(0,-2px,0); }
  }
  
  .modal-enter {
    animation: fadeIn 0.3s ease-out;
  }
  
  .table-row-hover {
    transition: all 0.2s ease;
  }
  
  .table-row-hover:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  
  .button-hover {
    transition: all 0.2s ease;
  }
  
  .button-hover:hover {
    transform: translateY(-1px);
  }
  
  .loading-spinner {
    animation: spin 1s linear infinite;
  }
  
  .fade-in {
    animation: fadeIn 0.5s ease-out;
  }
  
  .slide-in {
    animation: slideIn 0.3s ease-out;
  }
  
  .pulse {
    animation: pulse 2s infinite;
  }
  
  .bounce {
    animation: bounce 1s;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

const libraries = ['places', 'marker'];

function EvacuationShelters() {
  const navigate = useNavigate();
  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ 
    shelter_name: '',
    address: '',
    barangay: '',
    capacity: 0,
    contact_number: '',
    shelter_image1: '',
    shelter_image2: '',
    shelter_image3: '',
    shelter_image4: '',
    barangay_manager: '',
    status: 'active',
    latitude: null,
    longitude: null,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [imageFiles, setImageFiles] = useState([null, null, null, null]);
  const [barangayManagers, setBarangayManagers] = useState([]);
  const autocompleteRef = useRef(null);
  const [selectedShelters, setSelectedShelters] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const itemsPerPage = 10
  const [page, setPage] = useState(1);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryShelterIds, setSummaryShelterIds] = useState([]);
  const [summaryEvacueeIds, setSummaryEvacueeIds] = useState([]);
  const [summaryDateRange, setSummaryDateRange] = useState({ from: '', to: '' });
  const [summaryFamilyIdRange, setSummaryFamilyIdRange] = useState({ from: '', to: '' });
  const [summaryEvacuees, setSummaryEvacuees] = useState([]);
  const [summaryLogs, setSummaryLogs] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [contactError, setContactError] = useState('');
  const [summaryCustomTitle, setSummaryCustomTitle] = useState('');
  const [summaryActiveTab, setSummaryActiveTab] = useState('logs');
  const [allEvacuees, setAllEvacuees] = useState([]);
  const [recommendedEvacuee, setRecommendedEvacuee] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA',
    libraries: ['places', 'marker'],
  });

  useEffect(() => {
    document.title = "Evacuation Shelters";
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchShelters();
      fetchManagers();
    }

    const sheltersChannel = supabase
      .channel('public:evacuation_shelters_page') // Unique channel name
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'evacuation_shelters' },
        (payload) => {
          console.log('Evacuation shelters change received!', payload);
          fetchShelters();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sheltersChannel);
    };
  }, [currentUser]);

  useEffect(() => {
    if (showSummaryModal) {
      fetchSummaryEvacueesAndLogs();
    } else {
      setSummaryActiveTab('logs');
    }
    // eslint-disable-next-line
  }, [showSummaryModal]);

  async function fetchManagers() {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .eq('role', 'barangay');

    if (error) {
      console.error('Error fetching managers:', error);
    } else {
      setBarangayManagers(data || []);
    }
  }

  async function fetchShelters() {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    let query = supabase
      .from('evacuation_shelters')
      .select('*, profiles!barangay_manager(first_name, last_name)');

    if (currentUser.role === 'barangay') {
      query = query.eq('barangay_manager', currentUser.id);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) {
      setError('Failed to load shelters');
      setShelters([]);
    } else {
      setShelters(data || []);
    }
    setLoading(false);
  }

  async function fetchSummaryEvacueesAndLogs() {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      // Fetch all evacuees
      const { data: evacueesData, error: evacueesError } = await supabase
        .from('evacuees')
        .select('*');
      if (evacueesError) throw evacueesError;
      setSummaryEvacuees(evacueesData || []);
      // Fetch all logs
      const { data: logsData, error: logsError } = await supabase
        .from('evacuee_check_logs')
        .select('*');
      if (logsError) throw logsError;
      setSummaryLogs(logsData || []);
      // Initial filter
      filterLogs(evacueesData || [], logsData || []);
    } catch (err) {
      setSummaryError('Failed to load evacuees or logs.');
      setSummaryEvacuees([]);
      setSummaryLogs([]);
      setFilteredLogs([]);
    } finally {
      setSummaryLoading(false);
    }
  }

  function filterLogs(evacuees, logs) {
    let filtered = logs;
    // Shelter filter
    if (summaryShelterIds.length > 0 && !(summaryShelterIds.length === 1 && summaryShelterIds[0] === "__ALL__")) {
      filtered = filtered.filter(log => summaryShelterIds.includes(log.shelter_id));
    }
    // Evacuee filter
    if (summaryEvacueeIds.length > 0 && !(summaryEvacueeIds.length === 1 && summaryEvacueeIds[0] === "")) {
      filtered = filtered.filter(log => summaryEvacueeIds.includes(log.evacuee_id));
    }
    // Date range filter (applies to both check-in and check-out)
    if (summaryDateRange.from) {
      filtered = filtered.filter(log =>
        (log.check_in && log.check_in >= summaryDateRange.from) ||
        (log.check_out && log.check_out >= summaryDateRange.from)
      );
    }
    if (summaryDateRange.to) {
      const toDate = summaryDateRange.to + 'T23:59:59';
      filtered = filtered.filter(log =>
        (log.check_in && log.check_in <= toDate) ||
        (log.check_out && log.check_out <= toDate)
      );
    }
    // Family ID range
    if (summaryFamilyIdRange.from || summaryFamilyIdRange.to) {
      filtered = filtered.filter(log => {
        const evac = evacuees.find(e => e.id === log.evacuee_id);
        if (!evac) return false;
        const famId = evac.family_id || '';
        if (summaryFamilyIdRange.from && famId < summaryFamilyIdRange.from) return false;
        if (summaryFamilyIdRange.to && famId > summaryFamilyIdRange.to) return false;
        return true;
      });
    }
    setFilteredLogs(filtered);
  }

  function handleApplySummaryFilters() {
    filterLogs(summaryEvacuees, summaryLogs);
  }

  function getEvacueeName(evacueeId) {
    const evac = summaryEvacuees.find(e => e.id === evacueeId);
    return evac ? `${evac.first_name} ${evac.last_name}` : evacueeId;
  }
  function getFamilyId(evacueeId) {
    const evac = summaryEvacuees.find(e => e.id === evacueeId);
    return evac ? evac.family_id : '';
  }
  function getShelterName(shelterId) {
    const shelter = shelters.find(s => s.id === shelterId);
    return shelter ? shelter.shelter_name : shelterId;
  }

  const summaryInsights = useMemo(() => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return {
        totalRecords: 0,
        uniqueEvacuees: 0,
        uniqueShelters: 0,
        currentlySheltered: 0,
        avgStayHours: 0,
        lastActivity: null,
        topShelters: [],
        maxShelterCount: 0,
      };
    }

    const evacueeSet = new Set();
    const activeEvacuees = new Set();
    const shelterCounts = new Map();
    const durations = [];
    let lastActivity = null;

    filteredLogs.forEach(log => {
      if (log.evacuee_id) {
        evacueeSet.add(log.evacuee_id);
      }
      if (log.shelter_id) {
        shelterCounts.set(
          log.shelter_id,
          (shelterCounts.get(log.shelter_id) || 0) + 1
        );
      }

      if (log.check_in && !log.check_out) {
        activeEvacuees.add(log.evacuee_id);
      }

      if (log.check_in && log.check_out) {
        const inTime = new Date(log.check_in).getTime();
        const outTime = new Date(log.check_out).getTime();
        if (!Number.isNaN(inTime) && !Number.isNaN(outTime) && outTime >= inTime) {
          durations.push(outTime - inTime);
        }
      }

      ['check_in', 'check_out'].forEach(key => {
        if (log[key]) {
          const timestamp = new Date(log[key]).getTime();
          if (!Number.isNaN(timestamp)) {
            if (lastActivity === null || timestamp > lastActivity) {
              lastActivity = timestamp;
            }
          }
        }
      });
    });

    const avgStayHours = durations.length
      ? durations.reduce((acc, curr) => acc + curr, 0) / durations.length / (1000 * 60 * 60)
      : 0;

    const topShelters = Array.from(shelterCounts.entries())
      .map(([id, count]) => ({
        id,
        name: getShelterName(id),
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    const maxShelterCount = topShelters.length > 0 ? topShelters[0].count : 0;

    return {
      totalRecords: filteredLogs.length,
      uniqueEvacuees: evacueeSet.size,
      uniqueShelters: shelterCounts.size,
      currentlySheltered: activeEvacuees.size,
      avgStayHours,
      lastActivity: lastActivity ? new Date(lastActivity) : null,
      topShelters,
      maxShelterCount,
    };
  }, [filteredLogs, shelters]);

  const evacueeSummaries = useMemo(() => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return [];
    }

    const evacueeMap = new Map();

    filteredLogs.forEach(log => {
      const existing = evacueeMap.get(log.evacuee_id) || {
        evacueeId: log.evacuee_id,
        name: getEvacueeName(log.evacuee_id),
        visits: 0,
        lastCheckIn: null,
        lastCheckOut: null,
        lastActivityTs: null,
        recentShelterId: null,
        currentShelterId: null,
        status: 'Checked Out',
      };

      existing.visits += 1;

      const checkInTs = log.check_in ? new Date(log.check_in).getTime() : null;
      const checkOutTs = log.check_out ? new Date(log.check_out).getTime() : null;

      if (checkInTs && !Number.isNaN(checkInTs)) {
        if (!existing.lastCheckIn || checkInTs > existing.lastCheckIn) {
          existing.lastCheckIn = checkInTs;
          existing.currentShelterId = log.shelter_id || null;
          existing.recentShelterId = log.shelter_id || existing.recentShelterId;
        }
        if (!existing.lastActivityTs || checkInTs > existing.lastActivityTs) {
          existing.lastActivityTs = checkInTs;
        }
        existing.status = 'Sheltered';
      }

      if (checkOutTs && !Number.isNaN(checkOutTs)) {
        if (!existing.lastCheckOut || checkOutTs > existing.lastCheckOut) {
          existing.lastCheckOut = checkOutTs;
        }
        if (!existing.lastActivityTs || checkOutTs > existing.lastActivityTs) {
          existing.lastActivityTs = checkOutTs;
        }
        if (!checkInTs || checkOutTs >= (existing.lastCheckIn || checkOutTs)) {
          existing.status = 'Checked Out';
          existing.currentShelterId = null;
          if (log.shelter_id) {
            existing.recentShelterId = log.shelter_id;
          }
        }
      }

      evacueeMap.set(log.evacuee_id, existing);
    });

    return Array.from(evacueeMap.values())
      .map(item => ({
        ...item,
        lastCheckIn: item.lastCheckIn ? new Date(item.lastCheckIn) : null,
        lastCheckOut: item.lastCheckOut ? new Date(item.lastCheckOut) : null,
        lastActivity: item.lastActivityTs ? new Date(item.lastActivityTs) : null,
        currentShelterName: item.currentShelterId ? getShelterName(item.currentShelterId) : '—',
        recentShelterName: item.recentShelterId ? getShelterName(item.recentShelterId) : '—',
      }))
      .sort((a, b) => (b.lastActivity?.getTime() || 0) - (a.lastActivity?.getTime() || 0));
  }, [filteredLogs, summaryEvacuees, shelters]);

  const avgStayDisplay = summaryInsights.avgStayHours > 0
    ? summaryInsights.avgStayHours < 1
      ? `${Math.round(summaryInsights.avgStayHours * 60)} mins`
      : `${summaryInsights.avgStayHours.toFixed(1)} hrs`
    : '—';

  const lastActivityDisplay = summaryInsights.lastActivity
    ? summaryInsights.lastActivity.toLocaleString()
    : '—';

  const logsContainerMinHeight = filteredLogs.length > 0 ? Math.min(200, filteredLogs.length * 50 + 50) : 200;
  const overviewContainerMinHeight = evacueeSummaries.length > 0 ? Math.min(220, evacueeSummaries.length * 82 + 80) : 220;
  const shouldScrollLogs = filteredLogs.length > 10;
  const shouldScrollOverview = evacueeSummaries.length > 6;

  function handleExportPDF() {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 20;
    const palette = {
      primary: [23, 37, 84],
      secondary: [71, 85, 105],
      subtle: [120, 144, 156],
      divider: [226, 232, 240],
      fill: [249, 250, 251],
    };
    const setTextColor = (color) => doc.setTextColor(color[0], color[1], color[2]);
    const setDrawColor = (color) => doc.setDrawColor(color[0], color[1], color[2]);

    const pdfTitle = summaryCustomTitle && summaryCustomTitle.trim()
      ? summaryCustomTitle.trim()
      : 'Evacuation Logs Summary';

    const formatDateRange = (from, to) => {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const format = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return '';
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      };
      if (from && to) return `${format(from)} - ${format(to)}`;
      if (from) return `From ${format(from)}`;
      if (to) return `Until ${format(to)}`;
      return '';
    };

    const dateRangeStr = formatDateRange(summaryDateRange.from, summaryDateRange.to);
    const now = new Date();
    const generatedOn = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
    const generatedBy = (() => {
      if (!currentUser) return '—';
      const name = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim();
      if (name) return name;
      if (currentUser.role) {
        return currentUser.role.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
      }
      return '—';
    })();

    let cursorY = 26;

    try {
      const logo1 = `${window.location.origin}/GabaySakunaLogo.png`;
      doc.addImage(logo1, 'PNG', marginX, cursorY - 14, 14, 14);
    } catch (error) {
      console.warn('Failed to load primary logo for export.', error);
    }

    try {
      const logo2 = `${window.location.origin}/LegzapiCDRRMO.png`;
      doc.addImage(logo2, 'PNG', pageWidth - marginX - 14, cursorY - 14, 14, 14);
    } catch (error) {
      console.warn('Failed to load secondary logo for export.', error);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    setTextColor(palette.primary);
    doc.text('GabaySakuna', pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    setTextColor(palette.secondary);
    doc.text(pdfTitle, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += 6;

    setDrawColor(palette.divider);
    doc.setLineWidth(0.25);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 8;

    const lineHeight = 6;
    const metaLeft = [
      ['Report Period', dateRangeStr || 'Not specified'],
      ['Generated On', generatedOn],
    ];
    const metaRight = [
      ['Generated By', generatedBy],
      ['Currently Sheltered', `${summaryInsights.currentlySheltered}`],
    ];

    metaLeft.forEach((row, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(row[0], marginX, y);
      doc.setFont('helvetica', 'normal');
      setTextColor(palette.primary);
      doc.text(row[1], marginX + 32, y);
    });

    const rightColumnX = pageWidth / 2;
    metaRight.forEach((row, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(row[0], rightColumnX, y);
      doc.setFont('helvetica', 'normal');
      setTextColor(palette.primary);
      doc.text(row[1], rightColumnX + 32, y);
    });

    cursorY += lineHeight * Math.max(metaLeft.length, metaRight.length) + 6;
    setDrawColor(palette.divider);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 8;

    const summaryLines = [
      `Total records: ${summaryInsights.totalRecords}`,
      `Unique evacuees: ${summaryInsights.uniqueEvacuees}`,
      `Shelters covered: ${summaryInsights.uniqueShelters}`,
      `Average stay (hrs): ${summaryInsights.avgStayHours > 0 ? summaryInsights.avgStayHours.toFixed(1) : '—'}`,
    ];

    summaryLines.forEach((line, index) => {
      const y = cursorY + index * lineHeight;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setTextColor(palette.secondary);
      doc.text(`• ${line}`, marginX, y);
    });

    cursorY += summaryLines.length * lineHeight + 8;
    const tableStartY = cursorY;

    const tableBody = filteredLogs.map(log => [
      getEvacueeName(log.evacuee_id),
      getShelterName(log.shelter_id),
      log.check_in ? new Date(log.check_in).toLocaleString() : '—',
      log.check_out ? new Date(log.check_out).toLocaleString() : '—'
    ]);

    autoTable(doc, {
      head: [['Evacuee', 'Shelter', 'Check-in', 'Check-out']],
      body: tableBody,
      startY: tableStartY,
      margin: { left: marginX, right: marginX },
      styles: {
        fontSize: 9,
        cellPadding: 4,
        lineColor: palette.divider,
        lineWidth: 0.2,
        textColor: [71, 85, 105]
      },
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: palette.primary,
        fontStyle: 'bold',
        lineColor: palette.divider,
        lineWidth: 0.2,
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: palette.fill
      },
      didDrawPage: (data) => {
        const footerY = pageHeight - 12;
        setDrawColor(palette.divider);
        doc.setLineWidth(0.2);
        doc.line(marginX, footerY - 3, pageWidth - marginX, footerY - 3);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        setTextColor(palette.subtle);
        doc.text(`Generated by ${generatedBy} on ${generatedOn}`, marginX, footerY);
        doc.text(`Page ${data.pageNumber}`, pageWidth - marginX, footerY, { align: 'right' });
      }
    });

    let fileName = pdfTitle;
    if (dateRangeStr) {
      fileName += ` (${dateRangeStr})`;
    }
    fileName = fileName.replace(/[^a-zA-Z0-9 _\-()]/g, '').replace(/ +/g, ' ').trim();
    doc.save(`${fileName || 'evacuation-summary'}.pdf`);
  }

  function handlePlaceSelect() {
    if (autocompleteRef.current && autocompleteRef.current.getPlace()) {
      const place = autocompleteRef.current.getPlace();
      const address = place.formatted_address;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setForm(prev => ({
        ...prev,
        address: address,
        latitude: lat,
        longitude: lng
      }));
    }
  }

  function handleUseCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setForm(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
          }));

          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setForm(prev => ({ ...prev, address: results[0].formatted_address }));
            } else {
              console.error('Geocoder failed due to:', status);
              alert('Could not determine address from current location.');
            }
          });
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to retrieve your current location. Please ensure location services are enabled and granted permission.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  }

  async function handleImageUpload(event, index) {
    const file = event.target.files[0];
    if (file) {
      try {
        const newImageFiles = [...imageFiles];
        newImageFiles[index] = file;
        setImageFiles(newImageFiles);

        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('shelter-images')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          alert('Failed to upload image. Please try again.');
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('shelter-images')
          .getPublicUrl(filePath);

        setForm(prev => ({
          ...prev,
          [`shelter_image${index + 1}`]: publicUrl
        }));

      } catch (error) {
        console.error('Error in handleImageUpload:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  function openEdit(shelter) {
    setForm({
      shelter_name: shelter.shelter_name || '',
      address: shelter.address || '',
      barangay: shelter.barangay || '',
      capacity: shelter.capacity || 0,
      contact_number: shelter.contact_number || '',
      shelter_image1: shelter.shelter_image1 || '',
      shelter_image2: shelter.shelter_image2 || '',
      shelter_image3: shelter.shelter_image3 || '',
      shelter_image4: shelter.shelter_image4 || '',
      barangay_manager: shelter.barangay_manager || '',
      status: shelter.status || 'active',
      latitude: shelter.latitude || null,
      longitude: shelter.longitude || null,
    });
    setEditId(shelter.id);
    setShowForm(true);
  }

  async function handleAddNew(e) {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setContactError('');

    // Validate contact number (must be '+63' followed by 10 digits, e.g., +639123456789)
    const contactPattern = /^\+63\d{10}$/;
    if (!contactPattern.test(form.contact_number)) {
      setContactError('Please enter a valid mobile number (+639123456789)');
      setFormLoading(false);
      return;
    }

    if (form.latitude === null || form.longitude === null) {
      setFormError('Please provide a location by searching or using your current location.');
      setFormLoading(false);
      return;
    }

    try {
      let status = form.status;
      // If status is 'archived', keep as 'archived'
      // For other statuses, keep as provided by user

      const shelterData = {
        shelter_name: form.shelter_name,
        address: form.address,
        barangay: form.barangay,
        capacity: parseInt(form.capacity) || 0,
        contact_number: form.contact_number,
        shelter_image1: form.shelter_image1 || null,
        shelter_image2: form.shelter_image2 || null,
        shelter_image3: form.shelter_image3 || null,
        shelter_image4: form.shelter_image4 || null,
        barangay_manager: form.barangay_manager,
        status: status,
        latitude: form.latitude,
        longitude: form.longitude,
      };

      if (editId) {
        const { error } = await supabase.from('evacuation_shelters').update(shelterData).eq('id', editId);
        if (error) throw error;
        // Audit log for edit
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edited evacuation shelter',
          target_id: editId,
          target_type: 'EvacuationShelter',
          details: shelterData
        }]);
      } else {
        const { data: insertData, error } = await supabase.from('evacuation_shelters').insert([shelterData]).select();
        if (error) throw error;
        // Audit log for create
        if (insertData && insertData.length > 0) {
          await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'added evacuation shelter',
            target_id: insertData[0].id,
            target_type: 'EvacuationShelter',
            details: shelterData
          }]);
        }
      }
      setShowForm(false);
      setForm({
        shelter_name: '', address: '', barangay: '', capacity: 0,
        contact_number: '', shelter_image1: '', shelter_image2: '', shelter_image3: '',
        shelter_image4: '', barangay_manager: '', status: 'active', latitude: null, longitude: null,
      });
      setImageFiles([null, null, null, null]);
      setEditId(null);
      fetchShelters();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  }

  // Enhanced search logic: if search matches evacuees, recommend all their shelters
  const matchingEvacuees = search.trim()
    ? allEvacuees.filter(evac => {
        const fullName = `${evac.first_name || ''} ${evac.last_name || ''}`.trim().toLowerCase();
        const searchLower = search.toLowerCase();
        return (
          (evac.first_name && evac.first_name.toLowerCase().includes(searchLower)) ||
          (evac.last_name && evac.last_name.toLowerCase().includes(searchLower)) ||
          fullName.includes(searchLower)
        );
      })
    : [];
  const recommendedShelterIds = new Set(matchingEvacuees.map(evac => evac.shelter_id));
  useEffect(() => {
    setRecommendedEvacuee(null); // Clear previous recommendation
  }, [search, allEvacuees]);

  const filteredShelters = (() => {
    let result;
    if (search.trim() && matchingEvacuees.length > 0) {
      // Show all shelters that have a matching evacuee
      result = shelters.filter(shelter => recommendedShelterIds.has(shelter.id));
    } else {
      // Default: search by shelter name/location/contact
      result = shelters.filter(shelter => {
        const matchesSearch =
          search.trim() === '' ||
          (shelter.shelter_name && shelter.shelter_name.toLowerCase().includes(search.toLowerCase())) ||
          (shelter.address && shelter.address.toLowerCase().includes(search.toLowerCase())) ||
          (shelter.contact_number && shelter.contact_number.toLowerCase().includes(search.toLowerCase()));
        const matchesStatus =
          statusFilter === 'all' ||
          (shelter.status || '') === statusFilter;
        return matchesSearch && matchesStatus;
      });
    }
    // Debug logs
    console.log('allEvacuees:', allEvacuees);
    console.log('matchingEvacuees:', matchingEvacuees);
    console.log('recommendedShelterIds:', Array.from(recommendedShelterIds));
    console.log('filteredShelters:', result);
    return result;
  })();

  const totalPages = Math.ceil(filteredShelters.length / itemsPerPage);
  const paginatedShelters = filteredShelters.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  function toggleShelterSelection(id) {
    setSelectedShelters(prev => 
      prev.includes(id) 
        ? prev.filter(shelterId => shelterId !== id)
        : [...prev, id]
    );
  }

  async function handleArchiveSelected() {
    if (selectedShelters.length === 0) {
      alert('Please select shelters to archive');
      return;
    }

    try {
      const { error } = await supabase
        .from('evacuation_shelters')
        .update({ status: 'archived' })
        .in('id', selectedShelters);

      if (error) throw error;
      // Audit log for archive
      for (const id of selectedShelters) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'archived evacuation shelter',
          target_id: id,
          target_type: 'EvacuationShelter',
          details: { status: 'archived' }
        }]);
      }
      alert('Selected shelters archived successfully!');
      setSelectedShelters([]);
      fetchShelters();
    } catch (error) {
      console.error('Error archiving shelters:', error);
      alert(`Failed to archive shelters: ${error.message}`);
    }
  }

  useEffect(() => {
    // Fetch evacuees for all shelters (active, currently checked-in)
    async function fetchAllEvacuees() {
      try {
        console.log('Fetching all evacuees...');
        // Get all evacuees with status 'active'
        const { data: evacuees, error } = await supabase
          .from('evacuees')
          .select('*')
          .eq('status', 'active');
        if (error) {
          console.error('Error fetching evacuees:', error);
          setAllEvacuees([]);
          return;
        }
        // Get all logs for these evacuees
        const evacueeIds = evacuees.map(e => e.id);
        let logs = [];
        if (evacueeIds.length > 0) {
          const { data: logsData, error: logsError } = await supabase
            .from('evacuee_check_logs')
            .select('evacuee_id, shelter_id, check_in, check_out')
            .in('evacuee_id', evacueeIds);
          if (!logsError && logsData) logs = logsData;
        }
        // For each evacuee, find their latest log (by check_in desc)
        const latestLogMap = {};
        logs.forEach(log => {
          const curr = latestLogMap[log.evacuee_id];
          if (!curr || new Date(log.check_in) > new Date(curr.check_in)) {
            latestLogMap[log.evacuee_id] = log;
          }
        });
        // Only keep evacuees who are currently checked in (latest log has check_out === null)
        const checkedInEvacuees = evacuees.filter(e => {
          const log = latestLogMap[e.id];
          return log && log.check_in && !log.check_out;
        }).map(e => ({ ...e, shelter_id: latestLogMap[e.id]?.shelter_id }));
        console.log('Checked-in evacuees:', checkedInEvacuees);
        setAllEvacuees(checkedInEvacuees);
      } catch (err) {
        console.error('Error in fetchAllEvacuees:', err);
        setAllEvacuees([]);
      }
    }
    fetchAllEvacuees();
  }, []); // Only run once on mount

  if (!isLoaded) {
    return <Preloader text='Loading Shelters...' />;
  }

  console.log('autoTable' in jsPDF.prototype); // Should be true

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Evacuation Shelters</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div className="search-actions-row">
          {/* Search and Filter */}
          <div className="search-filters">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search by shelter name, location, or evacuee name"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="type-filter"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="Low">Low</option>
              <option value="Moderate">Moderate</option>
              <option value="Crowded">Crowded</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          {/* Buttons now beside the filter */}
          <div className="action-buttons">
            {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin') && (
              <>
                <button 
                  onClick={() => setShowForm(true)}
                  className="create-button"
                >
                  <FaPlus /> Add New Shelter
                </button>
                <button 
                  onClick={handleArchiveSelected}
                  disabled={selectedShelters.length === 0}
                  className="archive-button"
                >
                  <FaTrash /> Archive Selected
                </button>
              </>
            )}
            <button
              onClick={() => setShowSummaryModal(true)}
              className="summary-button"
            >
              Printable Logs/Summary
            </button>
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', minHeight: 200, overflowX: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#d32f2f' }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>
                    <input
                      type="checkbox"
                      checked={selectedShelters.length === filteredShelters.length && filteredShelters.length > 0}
                      onChange={e =>
                        setSelectedShelters(
                          e.target.checked ? filteredShelters.map(s => s.id) : []
                        )
                      }
                    />
                  </th>
                  <th style={{ padding: 12 }}>Image</th>
                  <th style={{ padding: 12 }}>Shelter Name</th>
                  <th style={{ padding: 12 }}>Address</th>
                  <th style={{ padding: 12 }}>Contact No.</th>
                  <th style={{ padding: 12 }}>Capacity</th>
                  <th style={{ padding: 12 }}>Current Occupants</th>
                  <th style={{ padding: 12 }}>Manager Name</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Remaining Capacity</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedShelters.length > 0 ? paginatedShelters.map(shelter => (
                  <tr key={shelter.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => navigate(`/evacuation-monitoring/${shelter.id}`)}>
                    <td style={{ padding: 12 }}>
                      <input
                        type="checkbox"
                        checked={selectedShelters.includes(shelter.id)}
                        onChange={e => {
                          e.stopPropagation();
                          toggleShelterSelection(shelter.id);
                        }}
                        onClick={e => e.stopPropagation()}
                        title="Archive Shelter"
                      />
                    </td>
                    <td style={{ padding: 12 }}>
                      <img src={shelter.shelter_image1 || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'} alt="shelter" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', background: '#eee' }} />
                    </td>
                    <td style={{ padding: 12 }}>{shelter.shelter_name}</td>
                    <td style={{ padding: 12 }}>{shelter.address}</td>
                    <td style={{ padding: 12 }}>{shelter.contact_number}</td>
                    <td style={{ padding: 12 }}>{shelter.capacity}</td>
                    <td style={{ padding: 12 }}>{shelter.accepted_occupants}</td>
                    <td style={{ padding: 12 }}>
                      {shelter.profiles ? `${shelter.profiles.first_name} ${shelter.profiles.last_name}` : 'N/A'}
                    </td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        background:
                          shelter.status === 'archived'
                            ? '#fce8e6'
                            : shelter.status === 'Low'
                            ? '#e6f4ea'
                            : shelter.status === 'Moderate'
                            ? '#fff3cd'
                            : shelter.status === 'Crowded'
                            ? '#f8d7da'
                            : '#e6f4ea',
                        color:
                          shelter.status === 'archived'
                            ? '#d32f2f'
                            : shelter.status === 'Low'
                            ? '#1e7e34'
                            : shelter.status === 'Moderate'
                            ? '#856404'
                            : shelter.status === 'Crowded'
                            ? '#721c24'
                            : '#1e7e34',
                        border: '1px solid #eee',
                        minWidth: 70,
                        textAlign: 'center'
                      }}>{shelter.status}</span>
                    </td>
                    <td style={{ padding: 12 }}>
                      {shelter.capacity - shelter.accepted_occupants}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div className="actions-cell">
                        {(currentUser?.role === 'admin' || currentUser?.role === 'super admin') && (
                          <button 
                            className="burger-menu-button" 
                            onClick={e => {
                              e.stopPropagation();
                              openEdit(shelter);
                            }}
                          >
                            <FaPen style={{ fontSize: 20, color: '#555' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No evacuation shelters found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
          >
            Previous
          </button>
          <div style={{ display: 'flex', gap: 4 }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  background: page === i + 1 ? '#08b1c7' : '#fff',
                  color: page === i + 1 ? '#fff' : '#333',
                  fontWeight: page === i + 1 ? 'bold' : 'normal'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
          >
            Next
          </button>
        </div>
        {showForm && isLoaded && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              minWidth: 0,
              maxWidth: 600,
              width: '100%',
              height: 600,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)'
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexShrink: 0 }}>
                <h2 style={{ margin: 0, fontSize: 24 }}>{editId ? 'Edit' : 'Add'} Evacuation Shelter</h2>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditId(null); setFormError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888',
                    marginLeft: 8
                  }}
                >×</button>
              </div>
              {/* Scrollable Form Fields */}
              <form
                onSubmit={handleAddNew}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Shelter Name</label>
                  <input
                    type="text"
                    placeholder="Shelter Name"
                    value={form.shelter_name}
                    onChange={e => setForm({ ...form, shelter_name: e.target.value })}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Address</label>
                  <Autocomplete
                    onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                    onPlaceChanged={handlePlaceSelect}
                  >
                    <input
                      type="text"
                      placeholder="Search for address"
                      value={form.address}
                      onChange={e => setForm(prev => ({ ...prev, address: e.target.value }))}
                      required
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', marginBottom: 8, maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                    />
                  </Autocomplete>
                  <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    style={{
                      marginTop: 2,
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: '1px solid #19c37d',
                      background: '#e6fff4',
                      color: '#19c37d',
                      cursor: 'pointer'
                    }}
                  >
                    Use Current Location
                  </button>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Barangay</label>
                  <select
                    value={form.barangay}
                    onChange={e => setForm({ ...form, barangay: e.target.value })}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  >
                    <option value="">Select Barangay</option>
                    {barangays.map(barangay => (
                      <option key={barangay.number} value={barangay.name}>{barangay.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Barangay Manager</label>
                  <select
                    value={form.barangay_manager}
                    onChange={e => setForm({ ...form, barangay_manager: e.target.value })}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  >
                    <option value="">Select a Manager</option>
                    {barangayManagers.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Contact Number</label>
                  <input
                    type="text"
                    placeholder="+639123456789"
                    value={form.contact_number}
                    onChange={e => {
                      let val = e.target.value;
                      if (!val.startsWith('+63')) {
                        val = '+63' + val.replace(/[^0-9]/g, '');
                      } else {
                        val = '+63' + val.slice(3).replace(/[^0-9]/g, '');
                      }
                      val = val.slice(0, 13);
                      setForm({ ...form, contact_number: val });
                      setContactError('');
                    }}
                    required
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  />
                  {contactError && <div style={{ color: '#d32f2f', marginTop: 4 }}>{contactError}</div>}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Capacity</label>
                  <input
                    type="number"
                    placeholder="Capacity"
                    value={form.capacity}
                    onChange={e => setForm({ ...form, capacity: e.target.value })}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Images (up to 4)</label>
                  {/* Single upload button for up to 4 images */}
                  {Array.from({ length: 4 }).some((_, i) => !form[`shelter_image${i + 1}`]) && (
                    <div>
                      <label
                        htmlFor="shelter-image-upload"
                        style={{
                          display: 'inline-block',
                          padding: '10px 20px',
                          background: '#fff',
                          color: '#19c37d',
                          border: '1px solid #19c37d',
                          borderRadius: 8,
                          cursor: 'pointer',
                          marginTop: 4
                        }}
                      >
                        Upload Images (up to 4)
                      </label>
                      <input
                        id="shelter-image-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={e => {
                          const files = Array.from(e.target.files).slice(0, 4);
                          files.forEach((file, idx) => {
                            // Find the first empty slot
                            const emptyIndex = [0, 1, 2, 3].find(i => !form[`shelter_image${i + 1}`]);
                            if (emptyIndex !== undefined) {
                              handleImageUpload({ target: { files: [file] } }, emptyIndex);
                            }
                          });
                        }}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 16,
                    position: 'relative',
                    maxWidth: 420,
                    margin: '0 auto'
                  }}>
                    {Array.from({ length: 4 }).map((_, index) =>
                      form[`shelter_image${index + 1}`] ? (
                        <div key={index} style={{ position: 'relative' }}>
                          <img
                            src={form[`shelter_image${index + 1}`]}
                            alt={`Preview ${index + 1}`}
                            style={{ width: '100%', height: 150, objectFit: 'cover', marginTop: 0, borderRadius: 4 }}
                          />
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                {formError && <div style={{ color: '#d32f2f', marginBottom: 8 }}>{formError}</div>}
              </form>
              {/* Modal Footer (fixed at bottom, outside scrollable area) */}
              <div style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'flex-end',
                marginTop: 24,
                flexShrink: 0,
                background: '#fff'
              }}>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditId(null); setFormError(''); }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 16
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="evacuation-shelter-form"
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#19c37d',
                    color: '#fff',
                    cursor: formLoading ? 'not-allowed' : 'pointer',
                    fontSize: 16,
                    fontWeight: 'bold',
                    opacity: formLoading ? 0.7 : 1
                  }}
                  disabled={formLoading}
                  onClick={() => document.querySelector('form').requestSubmit()}
                >
                  {formLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showSummaryModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(2px)'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 0,
              minWidth: 320,
              maxWidth: 1000,
              width: '90%',
              height: filteredLogs.length > 20 ? '85vh' : 'auto',
              maxHeight: '85vh',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative'
            }} className="modal-enter">
              {/* Simplified Header */}
              <div style={{
                background: '#08b1c7',
                padding: '20px 24px',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e8eaed'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: '#fff' }}>
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <h2 style={{ 
                    fontWeight: 600, 
                    fontSize: 20, 
                    color: '#fff', 
                    margin: 0
                  }}>
                    Evacuation Logs & Summary
                  </h2>
                </div>
                <button
                  onClick={() => setShowSummaryModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                  aria-label="Close"
                >×</button>
              </div>

              {/* Simplified Content */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                background: '#f8fafc'
              }}>
                
                {/* Simplified Filter Section */}
              <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: '20px',
                  border: '1px solid #e8eaed'
                }}>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: 16, 
                    fontWeight: 600, 
                    color: '#1a1a1a'
                  }}>
                    Filter Options
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: 16,
                    marginBottom: 16
                  }}>
                    {/* Custom Title */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 500, 
                        color: '#374151', 
                        marginBottom: 6,
                        fontSize: 14
                      }}>
                        Report Title
                      </label>
                    <input
                      type="text"
                      value={summaryCustomTitle || ''}
                      onChange={e => setSummaryCustomTitle(e.target.value)}
                        placeholder="Enter a custom title for the report..."
                        style={{ 
                          width: '100%', 
                          padding: '10px 12px', 
                          borderRadius: 6, 
                          border: '1px solid #d1d5db', 
                          fontSize: 14, 
                          boxSizing: 'border-box', 
                          background: '#fff',
                          transition: 'all 0.2s ease',
                          outline: 'none'
                        }}
                        onFocus={e => e.target.style.borderColor = '#08b1c7'}
                        onBlur={e => e.target.style.borderColor = '#d1d5db'}
                    />
                  </div>

                    {/* Shelter Filter */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 500, 
                        color: '#374151', 
                        marginBottom: 6,
                        fontSize: 14
                      }}>
                        Shelter(s) <span style={{color:'#e74c3c'}}>*</span>
                      </label>
                      <Select
                        options={[
                          { value: '__ALL__', label: 'All Shelters' },
                          ...shelters.map(shelter => ({
                            value: shelter.id,
                            label: shelter.shelter_name
                          }))
                        ]}
                        value={
                          summaryShelterIds.length === shelters.length
                            ? [{ value: '__ALL__', label: 'All Shelters' }, ...shelters.map(shelter => ({ value: shelter.id, label: shelter.shelter_name }))]
                            : shelters
                                .filter(shelter => summaryShelterIds.includes(shelter.id))
                                .map(shelter => ({ value: shelter.id, label: shelter.shelter_name }))
                        }
                        onChange={options => {
                          if (!options || options.length === 0) {
                            setSummaryShelterIds([]);
                          } else if (options.some(opt => opt.value === '__ALL__')) {
                            setSummaryShelterIds(shelters.map(s => s.id));
                          } else {
                            setSummaryShelterIds(options.map(opt => opt.value));
                          }
                        }}
                        isSearchable
                        isMulti
                        placeholder="Select or search shelter(s)..."
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        styles={{
                          container: base => ({ ...base, width: '100%' }),
                          control: (base, state) => ({ 
                            ...base, 
                            width: '100%', 
                            minHeight: 40,
                            borderRadius: 6,
                            border: state.isFocused ? '1px solid #08b1c7' : '1px solid #d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(8, 177, 199, 0.1)' : 'none',
                            transition: 'all 0.2s ease'
                          }),
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          menu: base => ({ 
                            ...base, 
                            zIndex: 9999,
                            borderRadius: 6,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            border: '1px solid #d1d5db'
                          }),
                          option: (base, state) => ({
                            ...base,
                            background: state.isSelected ? '#08b1c7' : state.isFocused ? '#f0f9ff' : 'transparent',
                            color: state.isSelected ? '#fff' : '#374151',
                            padding: '8px 12px',
                            cursor: 'pointer'
                          })
                        }}
                      />
                    </div>
                  </div>

                  {/* Date Range Section */}
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: 6,
                    padding: '16px',
                    marginBottom: 16
                  }}>
                    <h4 style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: '#374151'
                    }}>
                      Date Range
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr auto',
                      gap: 12,
                      alignItems: 'end'
                    }}>
                      <div>
                        <label style={{ 
                          fontWeight: 500, 
                          color: '#6b7280', 
                          marginBottom: 4,
                          fontSize: 12,
                          display: 'block'
                        }}>
                          From Date
                        </label>
                          <input
                            type="date"
                            value={summaryDateRange.from}
                            onChange={e => setSummaryDateRange(r => ({ ...r, from: e.target.value }))}
                          style={{ 
                            width: '100%', 
                            padding: '8px 10px', 
                            borderRadius: 6, 
                            border: '1px solid #d1d5db', 
                            fontSize: 14, 
                            boxSizing: 'border-box', 
                            background: '#fff',
                            transition: 'all 0.2s ease',
                            outline: 'none'
                          }}
                          onFocus={e => e.target.style.borderColor = '#08b1c7'}
                          onBlur={e => e.target.style.borderColor = '#d1d5db'}
                          />
                        </div>
                      
                      <div>
                        <label style={{ 
                          fontWeight: 500, 
                          color: '#6b7280', 
                          marginBottom: 4,
                          fontSize: 12,
                          display: 'block'
                        }}>
                          To Date
                        </label>
                          <input
                            type="date"
                            value={summaryDateRange.to}
                            onChange={e => setSummaryDateRange(r => ({ ...r, to: e.target.value }))}
                          style={{ 
                            width: '100%', 
                            padding: '8px 10px', 
                            borderRadius: 6, 
                            border: '1px solid #d1d5db', 
                            fontSize: 14, 
                            boxSizing: 'border-box', 
                            background: '#fff',
                            transition: 'all 0.2s ease',
                            outline: 'none'
                          }}
                          onFocus={e => e.target.style.borderColor = '#08b1c7'}
                          onBlur={e => e.target.style.borderColor = '#d1d5db'}
                          />
                        </div>

                      {/* Quick Range Buttons */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const from = new Date(today);
                          from.setDate(today.getDate() - 1);
                          setSummaryDateRange({ from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) });
                          setTimeout(handleApplySummaryFilters, 0);
                        }}
                          style={{ 
                            background: '#f0f9ff', 
                            color: '#08b1c7', 
                            border: '1px solid #b3d9ff', 
                            borderRadius: 4, 
                            padding: '6px 10px', 
                            fontWeight: 500, 
                            cursor: 'pointer',
                            fontSize: 12,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => e.target.style.background = '#e0f2fe'}
                          onMouseLeave={e => e.target.style.background = '#f0f9ff'}
                        >
                          Today
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const from = new Date(today);
                          from.setDate(today.getDate() - 7);
                          setSummaryDateRange({ from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) });
                          setTimeout(handleApplySummaryFilters, 0);
                        }}
                          style={{ 
                            background: '#f0f9ff', 
                            color: '#08b1c7', 
                            border: '1px solid #b3d9ff', 
                            borderRadius: 4, 
                            padding: '6px 10px', 
                            fontWeight: 500, 
                            cursor: 'pointer',
                            fontSize: 12,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => e.target.style.background = '#e0f2fe'}
                          onMouseLeave={e => e.target.style.background = '#f0f9ff'}
                        >
                          Week
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const from = new Date(today);
                          from.setMonth(today.getMonth() - 1);
                          setSummaryDateRange({ from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) });
                          setTimeout(handleApplySummaryFilters, 0);
                        }}
                          style={{ 
                            background: '#f0f9ff', 
                            color: '#08b1c7', 
                            border: '1px solid #b3d9ff', 
                            borderRadius: 4, 
                            padding: '6px 10px', 
                            fontWeight: 500, 
                            cursor: 'pointer',
                            fontSize: 12,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => e.target.style.background = '#e0f2fe'}
                          onMouseLeave={e => e.target.style.background = '#f0f9ff'}
                        >
                          Month
                      </button>
                    </div>
                  </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                  }}>
                    <button
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: 500,
                        borderRadius: 6,
                        background: '#08b1c7',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                      className="button-hover"
                      onClick={handleApplySummaryFilters}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                      Apply Filters
                    </button>
                    <button
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: 500,
                        borderRadius: 6,
                        background: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                      onMouseEnter={e => {
                        e.target.style.background = '#f9fafb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={e => {
                        e.target.style.background = 'transparent';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                      onClick={() => {
                        setSummaryShelterIds([]);
                        setSummaryEvacueeIds([]);
                        setSummaryDateRange({ from: '', to: '' });
                        setSummaryFamilyIdRange({ from: '', to: '' });
                        handleApplySummaryFilters();
                      }}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                      Reset
                    </button>
                </div>
              </div>

              {/* Summary Insight Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 16,
                marginBottom: 24
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  border: '1px solid #cfe8ff',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  boxShadow: '0 8px 20px rgba(8, 177, 199, 0.08)'
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0c4a6e' }}>Total Records</span>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{summaryInsights.totalRecords}</span>
                  <span style={{ fontSize: 12, color: '#0c4a6e' }}>Log entries included in this export</span>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  border: '1px solid #bbf7d0',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.08)'
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#047857' }}>Unique Evacuees</span>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#064e3b' }}>{summaryInsights.uniqueEvacuees}</span>
                  <span style={{ fontSize: 12, color: '#047857' }}>Individual evacuees represented</span>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)',
                  border: '1px solid #fde68a',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  boxShadow: '0 8px 20px rgba(245, 158, 11, 0.08)'
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#b45309' }}>Currently Sheltered</span>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#92400e' }}>{summaryInsights.currentlySheltered}</span>
                  <span style={{ fontSize: 12, color: '#b45309' }}>Evacuees without recorded checkout</span>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #f3f4ff 0%, #e0e7ff 100%)',
                  border: '1px solid #c7d2fe',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  boxShadow: '0 8px 20px rgba(79, 70, 229, 0.08)'
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#4338ca' }}>Avg. Stay Duration</span>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#312e81' }}>{avgStayDisplay}</span>
                  <span style={{ fontSize: 12, color: '#4338ca' }}>Across all completed visits</span>
                </div>
              </div>

              {summaryInsights.topShelters.length > 0 && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  padding: '20px',
                  marginBottom: 24
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Top Shelters by Activity</h4>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Last activity: <span style={{ fontWeight: 600, color: '#0f172a' }}>{lastActivityDisplay}</span></span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {summaryInsights.topShelters.map(shelter => {
                      const progress = summaryInsights.maxShelterCount
                        ? Math.round((shelter.count / summaryInsights.maxShelterCount) * 100)
                        : 0;
                      const width = Math.max(0, Math.min(progress, 100));
                      return (
                        <div key={shelter.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#0f172a' }}>
                            <span style={{ fontWeight: 600 }}>{shelter.name}</span>
                            <span style={{ color: '#0ea5e9', fontWeight: 600 }}>{shelter.count} logs</span>
                          </div>
                          <div style={{ height: 6, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                            <div style={{
                              width: `${width}%`,
                              height: '100%',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #08b1c7 100%)',
                              borderRadius: 999,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

                {/* Simplified Results Section */}
                <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: '20px',
                  border: '1px solid #e8eaed',
                  flex: 1,
                  minHeight: filteredLogs.length > 0 ? Math.min(300, filteredLogs.length * 50 + 100) : 300
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'stretch',
                    marginBottom: 16,
                    gap: 12,
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h3 style={{ 
                          margin: 0, 
                          fontSize: 16, 
                          fontWeight: 600, 
                          color: '#1a1a1a'
                        }}>
                          Results ({filteredLogs.length} records)
                        </h3>
                        {filteredLogs.length > 0 && (
                          <div style={{
                            background: '#f0f9ff',
                            color: '#0369a1',
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            boxShadow: '0 6px 16px rgba(14, 165, 233, 0.18)'
                          }}>
                            Ready to export
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <span>Last activity: <span style={{ fontWeight: 600, color: '#0f172a' }}>{lastActivityDisplay}</span></span>
                        <span style={{ color: '#cbd5f5' }}>•</span>
                        <span>Shelters involved: <span style={{ fontWeight: 600, color: '#0f172a' }}>{summaryInsights.uniqueShelters}</span></span>
                      </div>
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      padding: 4,
                      borderRadius: 999,
                      background: '#e0f2fe',
                      border: '1px solid #bae6fd',
                      boxShadow: 'inset 0 1px 2px rgba(8, 177, 199, 0.12)'
                    }}>
                      <button
                        type="button"
                        onClick={() => setSummaryActiveTab('logs')}
                        style={{
                          border: 'none',
                          background: summaryActiveTab === 'logs' ? '#08b1c7' : 'transparent',
                          color: summaryActiveTab === 'logs' ? '#fff' : '#0369a1',
                          padding: '6px 16px',
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: summaryActiveTab === 'logs' ? '0 6px 14px rgba(8, 177, 199, 0.25)' : 'none'
                        }}
                      >
                        Detailed Logs
                      </button>
                      <button
                        type="button"
                        onClick={() => setSummaryActiveTab('overview')}
                        style={{
                          border: 'none',
                          background: summaryActiveTab === 'overview' ? '#08b1c7' : 'transparent',
                          color: summaryActiveTab === 'overview' ? '#fff' : '#0369a1',
                          padding: '6px 16px',
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: summaryActiveTab === 'overview' ? '0 6px 14px rgba(8, 177, 199, 0.25)' : 'none'
                        }}
                      >
                        Evacuee Overview
                      </button>
                    </div>
                  </div>

                  <div style={{
                    background: '#f9fafb',
                    borderRadius: 6,
                    padding: '16px',
                    minHeight: summaryActiveTab === 'logs' ? logsContainerMinHeight : overviewContainerMinHeight,
                    maxHeight: summaryActiveTab === 'logs'
                      ? (shouldScrollLogs ? '50vh' : 'auto')
                      : (shouldScrollOverview ? '50vh' : 'auto'),
                    overflowX: 'auto',
                    overflowY: summaryActiveTab === 'logs'
                      ? (shouldScrollLogs ? 'auto' : 'visible')
                      : (shouldScrollOverview ? 'auto' : 'visible'),
                    border: '1px solid #e5e7eb'
                  }}>
                {summaryLoading ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 150,
                        color: '#6b7280',
                        fontSize: 14
                      }}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 8 }} className="loading-spinner">
                          <path d="M21 12a9 9 0 11-6.219-8.56"/>
                        </svg>
                        Loading results...
                      </div>
                ) : summaryError ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 150,
                        color: '#dc2626',
                        fontSize: 14,
                        background: '#fef2f2',
                        borderRadius: 6,
                        padding: 16
                      }}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        {summaryError}
                      </div>
                ) : summaryActiveTab === 'logs' ? (
                  filteredLogs.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 150,
                      color: '#6b7280',
                      fontSize: 14,
                      background: '#f9fafb',
                      borderRadius: 6,
                      padding: 16,
                      border: '1px dashed #d1d5db'
                    }}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      No results found. Try adjusting your filters.
                    </div>
                  ) : (
                      <div style={{
                        background: '#fff',
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb'
                      }}>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse', 
                          fontSize: 13,
                          minWidth: 500
                        }}>
                    <thead>
                            <tr style={{ 
                              background: '#08b1c7',
                              color: '#fff',
                              height: 40
                            }}>
                              <th style={{ 
                                padding: '10px 12px', 
                                textAlign: 'left',
                                fontWeight: 600,
                                fontSize: 13
                              }}>
                                Evacuee Name
                              </th>
                              <th style={{ 
                                padding: '10px 12px', 
                                textAlign: 'left',
                                fontWeight: 600,
                                fontSize: 13
                              }}>
                                Shelter
                              </th>
                              <th style={{ 
                                padding: '10px 12px', 
                                textAlign: 'left',
                                fontWeight: 600,
                                fontSize: 13
                              }}>
                                Check-in Time
                              </th>
                              <th style={{ 
                                padding: '10px 12px', 
                                textAlign: 'left',
                                fontWeight: 600,
                                fontSize: 13
                              }}>
                                Check-out Time
                              </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log, idx) => (
                              <tr key={log.id || idx} style={{ 
                                borderBottom: '1px solid #f3f4f6', 
                                background: idx % 2 === 0 ? '#fff' : '#f9fafb',
                                transition: 'all 0.2s ease',
                                height: 45
                              }}
                                 onMouseEnter={e => e.target.parentElement.style.background = '#f0f9ff'}
                                 onMouseLeave={e => e.target.parentElement.style.background = idx % 2 === 0 ? '#fff' : '#f9fafb'}
                                 className="table-row-hover"
                               >
                                <td style={{ 
                                  padding: '10px 12px',
                                  fontWeight: 500,
                                  color: '#1a1a1a'
                                }}>
                                  {getEvacueeName(log.evacuee_id)}
                                </td>
                                <td style={{ 
                                  padding: '10px 12px',
                                  color: '#6b7280'
                                }}>
                                  {getShelterName(log.shelter_id)}
                                </td>
                                <td style={{ 
                                  padding: '10px 12px',
                                  color: '#6b7280',
                                  fontFamily: 'monospace',
                                  fontSize: 12
                                }}>
                                  {log.check_in ? new Date(log.check_in).toLocaleString() : '-'}
                                </td>
                                <td style={{ 
                                  padding: '10px 12px',
                                  color: '#6b7280',
                                  fontFamily: 'monospace',
                                  fontSize: 12
                                }}>
                                  {log.check_out ? new Date(log.check_out).toLocaleString() : '-'}
                                </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                      </div>
                  )
                ) : evacueeSummaries.length === 0 ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 150,
                      color: '#6b7280',
                      fontSize: 14,
                      background: '#f9fafb',
                      borderRadius: 6,
                      padding: 16,
                      border: '1px dashed #d1d5db'
                    }}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                        <path d="M15 13l-4 4m0 0l-4-4m4 4V7"/>
                      </svg>
                      No evacuee summaries in the selected range.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {evacueeSummaries.map(summary => {
                        const isSheltered = summary.status === 'Sheltered';
                        return (
                          <div
                            key={summary.evacueeId}
                            style={{
                              background: '#fff',
                              borderRadius: 10,
                              border: '1px solid #e2e8f0',
                              padding: '14px 18px',
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 12,
                              alignItems: 'center',
                              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{summary.name}</div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                <span>Visits logged: <span style={{ fontWeight: 600, color: '#0f172a' }}>{summary.visits}</span></span>
                                <span style={{ color: '#cbd5f5' }}>•</span>
                                <span>Last activity: <span style={{ fontWeight: 600, color: '#0f172a' }}>{summary.lastActivity ? summary.lastActivity.toLocaleString() : '—'}</span></span>
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                                {isSheltered ? (
                                  <>Currently at <span style={{ fontWeight: 600, color: '#0f172a' }}>{summary.currentShelterName}</span></>
                                ) : (
                                  <>Last stayed at <span style={{ fontWeight: 600, color: '#0f172a' }}>{summary.recentShelterName}</span></>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                                Last check-in: {summary.lastCheckIn ? summary.lastCheckIn.toLocaleString() : '—'} • Last check-out: {summary.lastCheckOut ? summary.lastCheckOut.toLocaleString() : '—'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                              <span style={{
                                padding: '6px 12px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 600,
                                background: isSheltered ? 'rgba(34, 197, 94, 0.16)' : '#f1f5f9',
                                color: isSheltered ? '#15803d' : '#475569'
                              }}>
                                {isSheltered ? 'Sheltered' : 'Checked Out'}
                              </span>
                              <span style={{
                                fontSize: 11,
                                color: '#94a3b8'
                              }}>
                                Family ID: {getFamilyId(summary.evacueeId) || '—'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                )}
              </div>
              </div>
              </div>

              {/* Simplified Footer */}
              <div style={{
                background: '#fff',
                padding: '16px 24px',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                alignItems: 'center',
                borderTop: '1px solid #e8eaed'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginRight: 'auto',
                  color: '#6b7280',
                  fontSize: 13
                }}>
                  {filteredLogs.length} records selected
                </div>
                
                  <button
                  onClick={handleExportPDF} 
                    style={{
                    background: '#08b1c7',
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 6, 
                    padding: '10px 20px', 
                    fontSize: 14, 
                    fontWeight: 500, 
                      cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 120
                    }}
                  className="button-hover"
                  disabled={filteredLogs.length === 0}
                  >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Export PDF
                  </button>
                
                  <button
                  onClick={() => setShowSummaryModal(false)} 
                    style={{
                    background: '#fff',
                    color: '#6b7280',
                    border: '1px solid #d1d5db', 
                    borderRadius: 6, 
                    padding: '10px 20px', 
                    fontSize: 14, 
                    fontWeight: 500,
                      cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  Close
                  </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EvacuationShelters;
